/**
 * @file use-nav-blocker.ts
 * @description Кастомный хук для блокировки навигации в React Router v6.
 * Предоставляет механизм предотвращения навигации с подтверждением пользователя.
 * Использует UNSAFE_NavigationContext для доступа к history API и блокировки переходов.
 * Поддерживает условную активацию и управление через callback функции.
 * Реализует паттерн Latest Ref для корректной работы с замыканиями в useEffect.
 * @author AI Assistant
 * @created 2025-09-04
 * @updated 2025-09-04
 * @dependencies [history, react, react-router-dom]
 * @related-files []
 */

import { History } from 'history';
import { useContext, useEffect, useLayoutEffect, useRef } from 'react';
import { UNSAFE_NavigationContext } from 'react-router-dom';

interface NavBlockerControl {
    confirm: () => void;
    cancel: () => void;
}

interface NavBlocker {
    onBlock: (control: NavBlockerControl) => void;
    enabled?: boolean;
}

export const useNavBlocker = ({ onBlock, enabled }: NavBlocker) => {
    const { block } = useContext(UNSAFE_NavigationContext).navigator as History;
    // Latest ref pattern
    // Latest version of the function stored to the onBlockRef
    const onBlockRef = useRef(onBlock);
    useLayoutEffect(() => {
        onBlockRef.current = onBlock;
    });

    useEffect(() => {
        if (!enabled) {
            return;
        }

        let isActive = false;

        const unblock = block(({ retry }: any) => {
            if (isActive) {
                unblock();
                // Retry method handles navigation for us 🎉
                // Allows to simplify code even more.
                return retry();
            }

            // This doesn't need to be included in dependencies
            // and won't trigger useEffect
            onBlockRef.current({
                confirm: retry,
                cancel: () => {
                    isActive = false;
                }
            });

            isActive = true;
        });

        return unblock;
    }, [block, enabled]);
};
